/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact';
import { useContainerBreakpoints } from '#hooks/UNSAFE_useContainerBreakpoints';
import {
  useRichSelectionContext,
  type RichSelectionContextValue
} from '#PRIVATE_RichSelectionCommon/RichSelectionContext';

const sizes = ['xl', 'lg', 'md', 'sm', 'xs'] as const;

const columnMatrix = {
  xl: { xl: 6, md: 4, sm: 4 },
  lg: { xl: 4, md: 3, sm: 3 },
  md: { xl: 3, md: 2, sm: 2 },
  sm: { xl: 2, md: 2, sm: 2 },
  xs: { xl: 1, md: 1, sm: 1 }
} as const;

const breakpointSizes = {
  xs: '0px',
  sm: '600px',
  md: '768px',
  lg: '1024px',
  xl: '1440px'
} as const;

const breakpointColumns = (
  breakpoints: Record<(typeof sizes)[number], boolean>,
  layout: RichSelectionContextValue['layout']
) => {
  // incrementally go from largest to smallest breakpoint
  for (const size of sizes) {
    if (breakpoints[size]) {
      return columnMatrix[size][layout];
    }
  }
  // defaults to xs columns if no breakpoints are true
  return columnMatrix['xs'][layout];
};
/**
 * Hook to be used with rich selection items that are in a grid layout within RichSelectionField
 * @returns An object containing a ref to be placed on the container and a string
 * with the calculated grid template columns to be placed on the Grid component
 */
const useRichSelectionGrid = () => {
  const context = useRichSelectionContext()!;
  const { breakpointMatches, ref } = useContainerBreakpoints(breakpointSizes);

  return {
    ref: ref as Ref<HTMLDivElement>,
    gridTemplateColumns:
      context !== null ? `repeat(${breakpointColumns(breakpointMatches, context.layout)}, 1fr)` : ''
  };
};

export { useRichSelectionGrid };
